import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import {
  combinedErrorCodes,
  formatFactValues,
  requestFieldsFor,
  responseSummaryFor,
} from "./backend-api-coverage-detail-data";
import { fetchBackendApiCoverageSnapshot } from "./backend-api-coverage-fetch";
import { backendApiCoverageStyles as styles } from "./backend-api-coverage-styles";
import { TestCaseDetails } from "./backend-api-coverage-test-case-details";
import type {
  BackendApiCoverageSnapshot,
  BackendApiEndpoint,
  BackendApiTestCase,
} from "./backend-api-coverage-types";

export function BackendApiCoverageDetailPage(): React.JSX.Element {
  const { slug = "" } = useParams();
  const [snapshot, setSnapshot] = useState<BackendApiCoverageSnapshot | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setSnapshot(await fetchBackendApiCoverageSnapshot());
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const endpoint = useMemo(
    () => snapshot?.endpoints.find((item) => item.slug === slug) ?? null,
    [slug, snapshot],
  );
  const tests = useMemo(
    () => endpointTests(endpoint, snapshot?.tests ?? []),
    [endpoint, snapshot?.tests],
  );

  return (
    <div>
      <PageHeader
        title={endpoint?.id ?? t("api_coverage.detail_title")}
        description={endpoint?.title || t("api_coverage.detail_description")}
        actions={
          <Link to="/system/backend-api-coverage" style={styles.pagerButton}>
            {t("api_coverage.back_to_list")}
          </Link>
        }
      />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && <p style={shared.loadingText}>{t("common.loading")}</p>}
      {!loading && snapshot && !endpoint ? (
        <p style={shared.errorBanner}>{t("api_coverage.endpoint_not_found")}</p>
      ) : null}
      {endpoint ? <EndpointDetail endpoint={endpoint} tests={tests} /> : null}
    </div>
  );
}

export function EndpointDetail({
  endpoint,
  tests,
}: {
  endpoint: BackendApiEndpoint;
  tests: BackendApiTestCase[];
}): React.JSX.Element {
  const requestFields = requestFieldsFor(endpoint);
  const errorCodes = combinedErrorCodes(endpoint);
  return (
    <div style={styles.detailGrid}>
      <DocSection title={t("api_coverage.detail_overview")}>
        <MetaRow label={t("api_coverage.api_endpoint")} value={endpoint.id} />
        <MetaRow label={t("api_coverage.api_name")} value={endpoint.title} />
        <MetaRow label={t("api_coverage.module")} value={endpoint.module} />
        <MetaRow label={t("api_coverage.category")} value={endpoint.category} />
        <MetaRow
          label={t("api_coverage.test_status")}
          value={t(`api_coverage.status.${endpoint.status}`)}
        />
        <MetaRow
          label={t("api_coverage.frontend_exposure")}
          value={frontendExposureValue(endpoint)}
        />
        <MetaRow
          label={t("api_coverage.api_doc")}
          value={
            endpoint.doc.documented
              ? t("api_coverage.doc_found")
              : t("api_coverage.doc_missing")
          }
        />
      </DocSection>
      <DocSection title={t("api_coverage.detail_contract")}>
        <MetaRow
          label={t("api_coverage.auth")}
          value={endpoint.code.auth || t("api_coverage.doc_value_missing")}
        />
        <MetaRow
          label={t("api_coverage.status_codes")}
          value={
            endpoint.code.response_statuses.join(" / ") ||
            endpoint.doc.status_codes ||
            t("api_coverage.doc_value_missing")
          }
        />
        <MetaRow
          label={t("api_coverage.response")}
          value={responseSummaryFor(endpoint)}
        />
      </DocSection>
      <DocSection title={t("api_coverage.code_facts")}>
        <FactList
          label={t("api_coverage.middleware")}
          values={endpoint.code.middleware}
        />
        <FactList
          label={t("api_coverage.headers")}
          values={endpoint.code.headers}
        />
        <FactList
          label={t("api_coverage.path_params")}
          values={endpoint.code.path_params}
        />
        <FactList
          label={t("api_coverage.query_params")}
          values={endpoint.code.query_params}
        />
        <FactList
          label={t("api_coverage.body_reads")}
          values={formatFactValues(endpoint.code.body)}
        />
        <FactList
          label={t("api_coverage.validators")}
          values={endpoint.code.validators}
        />
        <FactList
          label={t("api_coverage.validation_checks")}
          values={formatFactValues(endpoint.code.validation_checks)}
        />
        <FactList
          label={t("api_coverage.response_facts")}
          values={formatFactValues(endpoint.code.response)}
        />
        <FactList
          label={t("api_coverage.response_json")}
          values={endpoint.code.response_json}
        />
        <FactList
          label={t("api_coverage.env_bindings")}
          values={endpoint.code.env_bindings}
        />
        <FactList
          label={t("api_coverage.service_calls")}
          values={endpoint.code.service_calls}
        />
        <FactList
          label={t("api_coverage.logic_steps")}
          values={formatFactValues(endpoint.code.logic_steps)}
        />
      </DocSection>
      {endpoint.frontend_exposure.calls.length > 0 ? (
        <DocSection title={t("api_coverage.frontend_calls")}>
          <FrontendCallList endpoint={endpoint} />
        </DocSection>
      ) : null}
      <DocSection title={t("api_coverage.request_fields")}>
        {requestFields.length > 0 ? (
          <RequestFields fields={requestFields} />
        ) : (
          <p style={styles.meta}>
            {endpoint.doc.request_summary ||
              t("api_coverage.doc_value_missing")}
          </p>
        )}
      </DocSection>
      <DocSection title={t("api_coverage.error_codes")}>
        {errorCodes.length > 0 ? (
          <div style={styles.codeList}>
            {errorCodes.map((code) => (
              <code key={code} style={styles.codePill}>
                {code}
              </code>
            ))}
          </div>
        ) : (
          <p style={styles.meta}>{t("api_coverage.no_error_codes")}</p>
        )}
      </DocSection>
      <DocSection title={t("api_coverage.linked_tests")}>
        {tests.length > 0 ? (
          <div style={styles.testList}>
            {tests.map((testCase) => (
              <TestCaseDetails key={testCase.id} testCase={testCase} />
            ))}
          </div>
        ) : (
          <p style={styles.meta}>{t("api_coverage.no_linked_tests")}</p>
        )}
      </DocSection>
    </div>
  );
}

function FrontendCallList({
  endpoint,
}: {
  endpoint: BackendApiEndpoint;
}): React.JSX.Element {
  return (
    <div style={styles.codeList}>
      {endpoint.frontend_exposure.calls.map((call) => (
        <code
          key={`${call.file}-${call.method}-${call.path}`}
          style={styles.codePill}
        >
          {call.method} {call.path} - {call.file}
        </code>
      ))}
    </div>
  );
}

function frontendExposureValue(endpoint: BackendApiEndpoint): string {
  if (endpoint.scope !== "admin") {
    return t("api_coverage.frontend_not_applicable");
  }
  if (!endpoint.frontend_exposure.exposed) {
    return t("api_coverage.frontend_unexposed");
  }
  return t("api_coverage.frontend_exposed", {
    count: endpoint.frontend_exposure.calls.length,
  });
}

function RequestFields({
  fields,
}: {
  fields: BackendApiEndpoint["doc"]["request_fields"];
}): React.JSX.Element {
  return (
    <table style={styles.detailTable}>
      <thead>
        <tr>
          <th style={styles.th}>{t("api_coverage.field_name")}</th>
          <th style={styles.th}>{t("api_coverage.field_type")}</th>
          <th style={styles.th}>{t("api_coverage.field_required")}</th>
          <th style={styles.th}>{t("api_coverage.field_description")}</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => (
          <tr key={`${field.name}-${field.description}`}>
            <td style={styles.td}>{field.name}</td>
            <td style={styles.td}>{field.type}</td>
            <td style={styles.td}>{field.required}</td>
            <td style={styles.td}>{field.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DocSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section style={styles.detailSection}>
      <h2 style={styles.sectionTitle}>{title}</h2>
      {children}
    </section>
  );
}

function MetaRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <p style={styles.metaRow}>
      <strong>{label}</strong>
      <span>{value}</span>
    </p>
  );
}

function FactList({
  label,
  values,
}: {
  label: string;
  values: string[];
}): React.JSX.Element {
  return (
    <p style={styles.metaRow}>
      <strong>{label}</strong>
      <span>
        {values.length > 0 ? values.join(", ") : t("api_coverage.none")}
      </span>
    </p>
  );
}

function endpointTests(
  endpoint: BackendApiEndpoint | null,
  tests: BackendApiTestCase[],
): BackendApiTestCase[] {
  if (!endpoint) return [];
  const ids = new Set(endpoint.tests.map((item) => item.test_id));
  return tests.filter((testCase) => ids.has(testCase.id));
}
